/**
 * MentionAutocomplete — Dropdown palette showing agent/user mention suggestions.
 *
 * Renders above the input bar via PalettePortal, with keyboard-navigable
 * items showing avatar, name, type badge, and email.
 */

import { AtSign, Bot, Building2, User, Paperclip } from 'lucide-react';
import type { MentionCandidate } from './hooks/useMentionAutocomplete';

interface MentionAutocompleteProps {
  candidates: MentionCandidate[];
  selectedIndex: number;
  accentColor: string;
  onSelect: (candidate: MentionCandidate) => void;
  onHover: (index: number) => void;
}

export function MentionAutocomplete({
  candidates,
  selectedIndex,
  accentColor,
  onSelect,
  onHover,
}: MentionAutocompleteProps) {
  return (
    <div
      className="mx-4 rounded-xl overflow-hidden"
      style={{
        background: 'white',
        border: '1px solid rgba(231,229,228,0.6)',
        boxShadow: '0 -4px 24px rgba(0,0,0,0.08), 0 -1px 4px rgba(0,0,0,0.04)',
        maxHeight: 240,
        overflowY: 'auto',
      }}
    >
      <div
        className="px-3 py-1.5 flex items-center gap-1.5"
        style={{
          borderBottom: '1px solid rgba(231,229,228,0.4)',
          background: 'rgba(250,250,249,0.5)',
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}
      >
        <AtSign size={10} style={{ color: accentColor }} />
        <span style={{ fontSize: 10, fontWeight: 600, color: '#a8a29e', letterSpacing: '0.05em', textTransform: 'uppercase' as const }}>
          Mention
        </span>
      </div>
      {candidates.map((c, i) => {
        const isSelected = i === selectedIndex;
        return (
          <button
            key={`${c.kind}-${c.id}`}
            onClick={() => onSelect(c)}
            onMouseEnter={() => onHover(i)}
            className="w-full flex items-center gap-3 px-3 py-2 text-left transition-colors duration-75"
            style={{
              background: isSelected ? `${accentColor}08` : 'transparent',
              borderLeft: isSelected ? `2px solid ${accentColor}` : '2px solid transparent',
            }}
          >
            <div
              className="flex items-center justify-center flex-shrink-0 rounded-full"
              style={{
                width: 26, height: 26,
                background: isSelected ? `${accentColor}12` : 'rgba(245,245,244,0.8)',
                fontSize: 10,
                fontWeight: 600,
                color: isSelected ? accentColor : '#78716c',
              }}
            >
              {c.avatar ? (
                <img src={c.avatar} alt="" className="w-full h-full rounded-full object-cover" />
              ) : c.kind === 'agent' ? (
                <Bot size={13} />
              ) : c.kind === 'file' ? (
                <Paperclip size={13} />
              ) : c.kind === 'company' ? (
                <Building2 size={13} />
              ) : c.kind === 'contact' ? (
                <User size={13} />
              ) : (
                c.name.slice(0, 2).toUpperCase()
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span style={{ fontSize: 12, fontWeight: 600, color: isSelected ? '#292524' : '#57534e' }}>
                  {c.name}
                </span>
                <span style={{ fontSize: 10, color: '#d6d3d1', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {c.kind}
                </span>
              </div>
              {c.email && (
                <span style={{ fontSize: 10, color: '#d6d3d1' }}>{c.email}</span>
              )}
            </div>
            {isSelected && (
              <kbd
                className="flex-shrink-0 font-mono"
                style={{ fontSize: 9, color: '#a8a29e', padding: '1px 5px', borderRadius: 4, background: 'rgba(245,245,244,0.8)', border: '1px solid rgba(231,229,228,0.5)' }}
              >
                Enter
              </kbd>
            )}
          </button>
        );
      })}
    </div>
  );
}
